const RETRYABLE_CLIENT_STATUSES = new Set([
  401, // A refreshed session may resolve an expired token.
  403, // Refreshed JWT claims may resolve a recent org/role change.
  408, // Request timeout.
  409, // TUS may use this for a transient upload conflict.
  423, // TUS upload lock.
  425, // Too early.
  429, // Rate limited.
])

/**
 * Supabase Storage's TUS endpoint (POST and PATCH) answers an expired JWT with
 * HTTP 400 rather than 401. The body carries the auth error:
 * `{"statusCode":"403","code":"AccessDenied","error":"Unauthorized",
 * "message":"\"exp\" claim timestamp check failed"}`. Only these markers turn
 * a 400 retryable; any other 400 is still a real request problem.
 */
const AUTH_ERROR_BODY_PATTERNS = [
  /claim timestamp check failed/i,
  /ERR_JWT_/,
  /\bjwt (?:expired|malformed)\b/i,
  /invalid jwt/i,
]

type HttpResponseLike = {
  getStatus?: () => unknown
  getBody?: () => unknown
}

type UploadErrorLike = {
  message?: unknown
  originalResponse?: HttpResponseLike | null
  status?: unknown
  statusCode?: unknown
}

export class AttachmentFileTooLargeError extends Error {
  readonly status = 413

  constructor(
    readonly size: number,
    readonly maxSize: number,
  ) {
    super(`Photo is too large (${size} bytes; maximum is ${maxSize} bytes)`)
    this.name = "AttachmentFileTooLargeError"
  }
}

/**
 * The photo row behind an upload no longer exists. saveFile writes the row and
 * the attachment in one transaction, so a missing row means the photo was
 * deleted before or during the upload and there is nothing left to send.
 */
export class AttachmentPhotoDeletedError extends Error {
  constructor(readonly attachmentId: string) {
    super(`Photo record not found for attachment ${attachmentId}`)
    this.name = "AttachmentPhotoDeletedError"
  }
}

/**
 * The photo row behind an upload is missing, but the local photos table can't
 * be trusted to say it was deleted: synced tables are empty after a local data
 * reset (or on first launch) until the first full sync lands. The queue keeps
 * the upload and retries it once the row arrives.
 */
export class AttachmentPhotoNotYetSyncedError extends Error {
  constructor(readonly attachmentId: string) {
    super(`Photo record for attachment ${attachmentId} has not synced yet`)
    this.name = "AttachmentPhotoNotYetSyncedError"
  }
}

export function getUploadErrorMessage(error: unknown): string {
  if (
    error &&
    typeof error === "object" &&
    typeof (error as UploadErrorLike).message === "string"
  ) {
    return (error as UploadErrorLike).message as string
  }
  return String(error)
}

export function getUploadErrorStatus(error: unknown): number | null {
  if (!error || typeof error !== "object") return null
  const candidate = error as UploadErrorLike

  try {
    const responseStatus = candidate.originalResponse?.getStatus?.()
    const parsedResponseStatus = Number(responseStatus)
    if (Number.isInteger(parsedResponseStatus) && parsedResponseStatus > 0) {
      return parsedResponseStatus
    }
  } catch {
    // Fall through to the serialized fields/message.
  }

  for (const value of [candidate.status, candidate.statusCode]) {
    const parsed = Number(value)
    if (Number.isInteger(parsed) && parsed > 0) return parsed
  }

  const match = getUploadErrorMessage(error).match(/response code:\s*(\d{3})/i)
  return match ? Number(match[1]) : null
}

function getUploadErrorBody(error: unknown): string | null {
  if (!error || typeof error !== "object") return null
  try {
    const body = (error as UploadErrorLike).originalResponse?.getBody?.()
    return typeof body === "string" ? body : null
  } catch {
    return null
  }
}

/**
 * True when Storage rejected the request's bearer token as a 400. A fresh
 * session fixes it, so it must not be recorded as a permanent failure.
 * The TUS error message embeds the response text, so it is the fallback when
 * the response object is gone (e.g. a serialized error).
 */
export function isAuthRejectedUploadError(error: unknown): boolean {
  if (getUploadErrorStatus(error) !== 400) return false
  const text = getUploadErrorBody(error) ?? getUploadErrorMessage(error)
  return AUTH_ERROR_BODY_PATTERNS.some((pattern) => pattern.test(text))
}

export function isPermanentAttachmentUploadError(error: unknown): boolean {
  const status = getUploadErrorStatus(error)
  return (
    status !== null &&
    status >= 400 &&
    status < 500 &&
    !RETRYABLE_CLIENT_STATUSES.has(status) &&
    !isAuthRejectedUploadError(error)
  )
}
